import { useEffect, useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  hydrateReportStore,
  selectActiveTab,
  selectBeatChanges,
  selectBeatOptions,
  selectBulkBeatsChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOptions,
  selectEventOthers,
  selectGroupEventChanges,
  selectSelectedDateValue,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

const keyRecoil_1 = KEY_RECOIL.BEAT_HR;
const keyRecoil_2 = KEY_RECOIL.RHYTHM_EVENT;
const keyRecoil_3 = KEY_RECOIL.STRIP_MANAGEMENT;
const keyRecoil_4 = KEY_RECOIL.DEVICE_MANAGEMENT;

/**
 * Rehydrates the report store from IndexedDB on mount and marks hydration complete once the restored state stops changing. Subscribes to every persisted slice - tab selections, beat and event changes, bookmarks, thumbnail sizes, strip and template changes - and debounces a one second timer that sets the loaded flag.
 *
 * @returns Nothing; this hook only runs the hydration effects.
 */
const useHydrateReportState = () => {
  const refTimer = useRef(null);

  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const setIsLoadedIndexDB = useReportStore((s) => s.actions.setIsLoadedIndexDB);
  const activeButton1 = useReportStore(selectActiveTab(keyRecoil_1));
  const activeButton2 = useReportStore(selectActiveTab(keyRecoil_2));
  const activeButton3 = useReportStore(selectActiveTab(keyRecoil_3));
  const beatChanges1 = useReportStore(selectBeatChanges(keyRecoil_1));
  const beatChanges2 = useReportStore(selectBeatChanges(keyRecoil_2));
  const selectedStrip1 = useReportStore(selectSelectedStrip(keyRecoil_1));
  const selectedStrip2 = useReportStore(selectSelectedStrip(keyRecoil_2));
  const selectedDateValue1 = useReportStore(selectSelectedDateValue(keyRecoil_1));
  const selectedDateValue2 = useReportStore(selectSelectedDateValue(keyRecoil_2));
  const selectedDateValue3 = useReportStore(selectSelectedDateValue(keyRecoil_3));
  const dataHeatmapChart = useReportStore((s) => s.dataHeatmapChart);
  const findings = useReportStore((s) => s.findings);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const selectedEcgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);
  const updatedDailySummaries = useReportStore((s) => s.updatedDailySummaries);
  const sizeThumbnailConfig1 = useReportStore((s) => s.sizeStripManagementThumbnailConfig);
  const sizeThumbnailConfig2 = useReportStore((s) => s.sizeStripDeviceThumbnailConfig);
  const sizeThumbnailConfig3 = useReportStore((s) => s.sizeEventThumbnailConfig);
  const sizeThumbnailConfig4 = useReportStore((s) => s.sizeBeatThumbnailConfig);
  const eventChanges1 = useReportStore(selectEventChanges(keyRecoil_1));
  const eventChanges2 = useReportStore(selectEventChanges(keyRecoil_2));
  const eventOthersState1 = useReportStore(selectEventOthers(keyRecoil_1));
  const eventOthersState2 = useReportStore(selectEventOthers(keyRecoil_2));
  const groupEventChanges1 = useReportStore(selectGroupEventChanges(keyRecoil_1));
  const groupEventChanges2 = useReportStore(selectGroupEventChanges(keyRecoil_2));
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  const eventNews1 = useReportStore(selectEventNews(keyRecoil_1));
  const eventNews2 = useReportStore(selectEventNews(keyRecoil_2));
  const changeStripData1 = useReportStore((s) => s.changeStripData[keyRecoil_3]);
  const changeStripData2 = useReportStore((s) => s.changeStripData[keyRecoil_4]);

  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil_1));
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);

  const eventOptionsState1 = useReportStore(selectEventOptions(keyRecoil_1));
  const eventOptionsState2 = useReportStore(selectEventOptions(keyRecoil_2));
  const eventOptionsState3 = useReportStore(selectEventOptions(keyRecoil_3));
  const eventOptionsState4 = useReportStore(selectEventOptions(keyRecoil_4));

  const beatOptionsState1 = useReportStore(selectBeatOptions(keyRecoil_1));
  const beatOptionsState2 = useReportStore(selectBeatOptions(keyRecoil_2));
  const beatOptionsState3 = useReportStore(selectBeatOptions(keyRecoil_3));
  const beatOptionsState4 = useReportStore(selectBeatOptions(keyRecoil_4));

  const swapBeatChanges = useReportStore((s) => s.swapBeatChanges[keyRecoil_1]);
  const bulkSwapBeats = useReportStore((s) => s.bulkSwapBeats[keyRecoil_1]);
  const resetSwapBeats = useReportStore((s) => s.resetSwapBeats[keyRecoil_1]);
  const templateChanges = useReportStore((s) => s.templateChanges[keyRecoil_1]);

  useEffect(() => {
    hydrateReportStore();
  }, []);

  useEffect(() => {
    if (isLoadedIndexDB) {
      return;
    }

    if (refTimer.current) {
      clearTimeout(refTimer.current);
    }
    refTimer.current = setTimeout(() => {
      setIsLoadedIndexDB(true);
    }, 1000);
  }, [
    activeButton1,
    activeButton2,
    beatChanges1,
    beatChanges2,
    dataHeatmapChart,
    findings,
    selectedStrip1,
    selectedStrip2,
    selectedDateValue1,
    selectedDateValue2,
    selectedDateValue3,
    ecgBookmarks,
    activeButton3,
    updatedDailySummaries,
    selectedEcgDisclosure,
    sizeThumbnailConfig1,
    sizeThumbnailConfig2,
    sizeThumbnailConfig3,
    sizeThumbnailConfig4,
    eventChanges1,
    eventChanges2,
    eventOthersState1,
    eventOthersState2,
    groupEventChanges1,
    groupEventChanges2,
    ecgBookmarkDeleteIds,
    eventNews1,
    eventNews2,
    changeStripData1,
    changeStripData2,
    isLoadedIndexDB,
    ecgDisclosureChange,
    eventOptionsState1,
    eventOptionsState2,
    eventOptionsState3,
    eventOptionsState4,
    beatOptionsState1,
    beatOptionsState2,
    beatOptionsState3,
    beatOptionsState4,
    swapBeatChanges,
    bulkBeatsChanges,
    templateChanges,
    bulkSwapBeats,
    resetSwapBeats,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    reviewedBeatsChanges,
  ]);
};

export default useHydrateReportState;
